import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  Pill, 
  Clock, 
  Check,
  CheckCircle2,
  Sun,
  Moon,
  Sunrise,
  Send,
  MessageCircle,
  Loader2
} from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper per fascia
const getFasciaInfo = (fascia) => {
  switch(fascia) {
    case 'MATTINA': 
      return { 
        icon: <Sunrise className="w-6 h-6" />, 
        emoji: '🌅',
        color: 'from-amber-400 to-orange-400',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-300',
        textColor: 'text-amber-700',
        orari: '00:00 - 12:00'
      };
    case 'POMERIGGIO': 
      return { 
        icon: <Sun className="w-6 h-6" />, 
        emoji: '☀️',
        color: 'from-orange-400 to-yellow-400',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-300',
        textColor: 'text-orange-700',
        orari: '12:00 - 18:00'
      };
    case 'SERA': 
      return { 
        icon: <Moon className="w-6 h-6" />, 
        emoji: '🌙',
        color: 'from-indigo-400 to-purple-400',
        bgColor: 'bg-indigo-50',
        borderColor: 'border-indigo-300',
        textColor: 'text-indigo-700',
        orari: '18:00 - 24:00'
      };
    default: 
      return { 
        icon: <Clock className="w-6 h-6" />, 
        emoji: '⏰',
        color: 'from-gray-400 to-gray-500',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-300',
        textColor: 'text-gray-700',
        orari: ''
      };
  }
};

// Componente Vassoio per singola fascia
const VassoioFascia = ({ fascia, farmaci, selectedIds, onToggle }) => {
  const [expanded, setExpanded] = useState(false);
  const info = getFasciaInfo(fascia);
  
  const farmaciNonPresi = farmaci.filter(f => !f.preso);
  const farmaciPresi = farmaci.filter(f => f.preso);
  const selectedInFascia = farmaci.filter(f => selectedIds.includes(f.id) && !f.preso).length;
  
  if (farmaci.length === 0) return null;
  
  return (
    <div className={`rounded-2xl border-2 overflow-hidden ${info.borderColor} ${info.bgColor}`}>
      {/* Header Fascia */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-5 py-4 flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${info.color} flex items-center justify-center text-white shadow-lg`}>
            {info.icon}
          </div>
          <div className="text-left">
            <h3 className={`font-bold text-lg ${info.textColor}`}>{fascia}</h3>
            <p className="text-sm text-gray-500">{info.orari}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Badge conteggio */}
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-bold ${
              farmaciNonPresi.length === 0 
                ? 'bg-green-100 text-green-700' 
                : 'bg-white text-gray-700'
            }`}>
              {farmaciPresi.length}/{farmaci.length}
            </span>
            {selectedInFascia > 0 && (
              <span className="px-2 py-1 rounded-full text-xs font-bold bg-[#4A7C59] text-white">
                {selectedInFascia} sel.
              </span>
            )}
          </div>
          
          {/* Chevron */}
          <span className={`transform transition-transform ${expanded ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </div>
      </button>
      
      {/* Lista Farmaci */}
      {expanded && (
        <div className="bg-white border-t divide-y">
          {farmaci.map(farmaco => {
            const isSelected = selectedIds.includes(farmaco.id);
            const isPreso = farmaco.preso;
            const orarioFormatted = farmaco.orario?.slice(0, 5);
            
            return (
              <div 
                key={farmaco.id}
                onClick={() => !isPreso && onToggle(farmaco.id)}
                className={`px-5 py-4 flex items-center gap-4 transition-all ${
                  isPreso 
                    ? 'bg-green-50 cursor-default'
                    : isSelected 
                      ? 'bg-[#4A7C59]/10 cursor-pointer active:bg-[#4A7C59]/20 active:scale-[0.98]' 
                      : 'hover:bg-gray-50 cursor-pointer active:bg-[#4A7C59]/10 active:scale-[0.98]'
                }`}
              >
                {/* Checkbox */}
                <div className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                  isPreso 
                    ? 'bg-green-500 border-green-500'
                    : isSelected 
                      ? 'bg-[#4A7C59] border-[#4A7C59]' 
                      : 'border-gray-300'
                }`}>
                  {(isSelected || isPreso) && <Check className="w-4 h-4 text-white" />}
                </div>
                
                {/* Info Farmaco: [Nome] - [Orario] ✅ */}
                <div className="flex-1 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold ${isPreso ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                      {farmaco.nome_farmaco}
                    </span>
                    {farmaco.dosaggio && (
                      <span className="text-sm text-gray-500">({farmaco.dosaggio})</span>
                    )}
                    <span className="text-gray-400">-</span>
                    <span className={`font-medium ${isPreso ? 'text-gray-400' : 'text-gray-700'}`}>
                      {orarioFormatted}
                    </span>
                    {isPreso && (
                      <span className="text-green-500 font-bold ml-1">✅</span>
                    )}
                  </div>
                  {isPreso && (
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Preso
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Componente principale - Vassoio Assistente (SOLO MEDICINE)
const MedicineTray = ({ famigliaId, nomeAssistente, nomeAssistito }) => {
  const [tray, setTray] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [nota, setNota] = useState('');
  const [sending, setSending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Carica vassoio
  const loadTray = useCallback(async () => {
    if (!famigliaId) return;
    
    try {
      const res = await fetch(`${API_URL}/api/medicine/tray/${famigliaId}`);
      if (res.ok) {
        const data = await res.json();
        setTray(data);
      }
    } catch (err) {
      console.error('Error loading tray:', err);
      toast.error('Errore caricamento terapie');
    } finally {
      setLoading(false);
    }
  }, [famigliaId]);
  
  useEffect(() => {
    loadTray();
  }, [loadTray]);
  
  // Toggle selezione farmaco
  const toggleSelection = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };
  // Registra attività medicine - con protezione da doppio click
  const handleSubmit = async () => {
    if (selectedIds.length === 0) {
      toast.error('Seleziona almeno una terapia');
      return;
    }
    
    // Previeni doppio submit
    if (sending) {
      console.log('Submit già in corso, ignorato');
      return;
    }
    
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/medicine/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          famiglia_id: famigliaId,
          assistente_nome: nomeAssistente || 'Assistente',
          medicine_ids: selectedIds,
          nota: nota || null
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setShowSuccess(true);
        setSelectedIds([]);
        setNota('');
        
        // Ricarica dopo 2 secondi
        setTimeout(() => {
          setShowSuccess(false);
          loadTray();
        }, 3000);
        
        toast.success(`${data.farmaci_registrati} farmaci registrati!`);
      } else {
        throw new Error('Errore registrazione');
      }
    } catch (err) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSending(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#4A7C59] mx-auto mb-4" />
          <p className="text-gray-500">Caricamento terapie...</p>
        </div>
      </div>
    );
  }
  
  if (showSuccess) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-green-700 mb-2">Attività Registrata!</h2>
          <p className="text-gray-500">La famiglia è stata notificata</p>
        </div>
      </div>
    );
  }
  
  if (!tray || tray.totale_farmaci === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Pill className="w-10 h-10 text-gray-400" />
        </div>
        <h2 className="text-xl font-bold text-gray-700 mb-2">Nessuna terapia programmata</h2>
        <p className="text-gray-500">Il piano terapie non è ancora stato configurato</p>
      </div>
    );
  }
  
  const selectedCount = selectedIds.length;
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-[#4A7C59] to-[#3d6549] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Pill className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-xl font-bold text-gray-900">Terapie di oggi</h2>
        <p className="text-gray-500 mt-1">
          Terapie per <strong>{nomeAssistito || 'oggi'}</strong>
        </p>
      </div>
      
      {/* Progress */}
      <div className="bg-white rounded-xl p-4 border shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600">Progresso giornaliero</span>
          <span className="font-bold text-[#4A7C59]">
            {tray.farmaci_presi}/{tray.totale_farmaci}
          </span>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#4A7C59] to-[#6B9B7A] transition-all duration-500"
            style={{ width: `${(tray.farmaci_presi / tray.totale_farmaci) * 100}%` }}
          />
        </div>
      </div>
      
      {/* Vassoi per fascia */}
      <div className="space-y-4">
        {['MATTINA', 'POMERIGGIO', 'SERA'].map(fascia => (
          <VassoioFascia
            key={fascia}
            fascia={fascia}
            farmaci={tray.tray[fascia]?.farmaci || []}
            selectedIds={selectedIds}
            onToggle={toggleSelection}
          />
        ))}
      </div>
      
      {/* Form Nota + Conferma */}
      {selectedCount > 0 && (
        <div className="bg-white rounded-2xl border-2 border-[#4A7C59] p-5 space-y-4 sticky bottom-4 shadow-xl">
          {/* Riepilogo selezione */}
          <div className="flex items-center gap-3 pb-3 border-b">
            <div className="w-10 h-10 bg-[#4A7C59] rounded-xl flex items-center justify-center text-white font-bold">
              {selectedCount}
            </div>
            <div>
              <p className="font-semibold text-gray-900">
                {selectedCount === 1 ? '1 terapia selezionata' : `${selectedCount} terapie selezionate`}
              </p>
              <p className="text-sm text-gray-500">Pronto per la registrazione</p>
            </div>
          </div>
          
          {/* Campo nota */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Aggiungi una nota (opzionale)
            </label>
            <textarea
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Es. Assunti dopo colazione, nessun problema..."
              rows={2}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#4A7C59] focus:outline-none resize-none"
            />
          </div>
          
          {/* Bottone conferma */}
          <Button
            onClick={handleSubmit}
            disabled={sending}
            className="w-full bg-[#4A7C59] hover:bg-[#3d6549] py-6 text-lg font-bold"
          >
            {sending ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Registra Attività
              </>
            )}
          </Button>
          
          <p className="text-xs text-center text-gray-400 flex items-center justify-center gap-1">
            <MessageCircle className="w-3 h-3" />
            La famiglia riceverà una notifica push
          </p>
        </div>
      )}
    </div>
  );
};

export default MedicineTray;
